import { UzButton, UzCard, UzInput, UzSelect, type UzSelectOption } from '@utazzo/ui';
import { Plus, Trash2 } from 'lucide-react';
import React, { useMemo } from 'react';

import {
  emptyAirlineSectorRow,
  emptyTrainBusRow,
  type AirlineSectorRow,
  type TrainBusRow,
} from './invoice-form.helpers';

import { useAirlinesList } from '@/features/airlines/use-airlines';

const FLIGHT_TYPE_OPTIONS: ReadonlyArray<UzSelectOption<string>> = [
  { value: 'DOM', label: 'Domestic' },
  { value: 'INT', label: 'International' },
];

interface TrainBusGridProps {
  mode: 'TRAIN' | 'TRANSFER';
  rows: TrainBusRow[];
  onChange: (rows: TrainBusRow[]) => void;
}

/** Section 7 - Train / Transfer sector (Quota column shown for TRAIN only). */
export const TrainBusGrid = ({ mode, rows, onChange }: TrainBusGridProps): React.JSX.Element => {
  const update = (idx: number, patch: Partial<TrainBusRow>): void =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const showQuota = mode === 'TRAIN';

  return (
    <UzCard
      title={`${mode === 'TRAIN' ? 'Train' : 'Transfer'} sector`}
      actions={
        <UzButton
          variant="outline"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => onChange([...rows, emptyTrainBusRow()])}
        >
          Add sector
        </UzButton>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">No sectors added.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row, idx) => (
            <div
              key={idx}
              className={`grid items-start gap-3 ${showQuota ? 'md:grid-cols-[1fr_1fr_1fr_auto]' : 'md:grid-cols-[1fr_1fr_auto]'}`}
            >
              <UzInput
                label={idx === 0 ? 'PNR' : undefined}
                value={row.pnr}
                onChange={(e) => update(idx, { pnr: e.target.value })}
              />
              <UzInput
                label={idx === 0 ? 'Booking class' : undefined}
                value={row.bookingClass}
                onChange={(e) => update(idx, { bookingClass: e.target.value })}
              />
              {showQuota && (
                <UzInput
                  label={idx === 0 ? 'Quota' : undefined}
                  value={row.quota}
                  onChange={(e) => update(idx, { quota: e.target.value })}
                />
              )}
              <div className={idx === 0 ? 'md:pt-6' : ''}>
                <UzButton
                  variant="ghost"
                  size="sm"
                  aria-label="Remove sector"
                  onClick={() => onChange(rows.filter((_, i) => i !== idx))}
                >
                  <Trash2 className="h-4 w-4" />
                </UzButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </UzCard>
  );
};

interface AirlineSectorGridProps {
  rows: AirlineSectorRow[];
  onChange: (rows: AirlineSectorRow[]) => void;
  showErrors: boolean;
  invalid: boolean;
}

/** Section 8 - Airline sector (flight-only, >=1 required). */
export const AirlineSectorGrid = ({
  rows,
  onChange,
  showErrors,
  invalid,
}: AirlineSectorGridProps): React.JSX.Element => {
  const update = (idx: number, patch: Partial<AirlineSectorRow>): void =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const airlinesQuery = useAirlinesList({
    page: 1,
    pageSize: 200,
    sortBy: 'name',
    sortDir: 'asc',
    isActive: true,
  });
  const airlineOptions = useMemo<ReadonlyArray<UzSelectOption>>(
    () => (airlinesQuery.data?.data ?? []).map((a) => ({ value: a.id, label: a.name })),
    [airlinesQuery.data],
  );

  return (
    <UzCard
      title="Airline sector"
      description="One row per e-ticket. At least one is required for flight-only invoices."
      actions={
        <UzButton
          variant="outline"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => onChange([...rows, emptyAirlineSectorRow()])}
        >
          Add sector
        </UzButton>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">
          {showErrors && invalid ? (
            <span className="text-red-600">Add at least one airline sector.</span>
          ) : (
            'No sectors added.'
          )}
        </p>
      ) : (
        <div className="space-y-5">
          {rows.map((row, idx) => (
            <div key={idx} className="rounded-md border border-zinc-100 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-500">Sector {idx + 1}</span>
                <UzButton
                  variant="ghost"
                  size="sm"
                  aria-label="Remove sector"
                  onClick={() => onChange(rows.filter((_, i) => i !== idx))}
                >
                  <Trash2 className="h-4 w-4" />
                </UzButton>
              </div>
              <div className="grid gap-3 md:grid-cols-4">
                <UzInput
                  label="Pax name"
                  value={row.paxName}
                  onChange={(e) => update(idx, { paxName: e.target.value })}
                />
                <UzInput
                  label="E-ticket"
                  value={row.eTicket}
                  onChange={(e) => update(idx, { eTicket: e.target.value })}
                />
                <UzInput
                  label="PNR"
                  value={row.pnr}
                  onChange={(e) => update(idx, { pnr: e.target.value })}
                />
                <UzSelect
                  label="Airline"
                  placeholder={airlinesQuery.isLoading ? 'Loading...' : 'Select airline'}
                  options={airlineOptions}
                  value={row.airlineId || undefined}
                  onValueChange={(v) => update(idx, { airlineId: v })}
                  disabled={airlinesQuery.isLoading}
                />
                <UzInput
                  label="Source"
                  value={row.source}
                  onChange={(e) => update(idx, { source: e.target.value })}
                />
                <UzSelect
                  label="Flight type"
                  placeholder="Type"
                  options={FLIGHT_TYPE_OPTIONS}
                  value={row.flightType || undefined}
                  onValueChange={(v) => update(idx, { flightType: v })}
                />
                <UzInput
                  label="Carrier type"
                  value={row.carrierType}
                  onChange={(e) => update(idx, { carrierType: e.target.value })}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </UzCard>
  );
};
